import React from "react";
import ReactPlayer from "react-player";
import { findDOMNode } from "react-dom";
import { reaction } from "mobx";
import { flow, Instance, types, getEnv, cast } from "mobx-state-tree";
import screenfull from "screenfull";

import { DoNotJumpTimestamp, emptyTimestamp, Timestamp, Timestamps } from "./TimestampsData";
import { endpoints } from "../api/ApiEndpoints";
import { api } from "../api/ApiService";
import { SecondaryCodeEnum } from "../../generated/Damages";
import { CodeDetection, DetectionsAtTime } from "../../api-models/response/VideoAnalysisDataResponse";

const doNotJumpTimestamp: Timestamp = new DoNotJumpTimestamp();

function getVideoElementFromReactPlayer(playerRef: React.RefObject<ReactPlayer>): HTMLVideoElement | null {
  const internalPlayer = playerRef.current?.getInternalPlayer?.();
  if (internalPlayer && internalPlayer instanceof HTMLVideoElement) {
    return internalPlayer;
  }
  return null;
}

export interface VADetection {
  mainCode: string;
  c1Code: string;
  c2Code: string;
  timestamp?: number;
}

export const emptyVADetection: VADetection = {
  mainCode: "",
  c1Code: "",
  c2Code: "",
  timestamp: undefined,
};

const VideoPlayerModel = types.model({
  videoProgress: types.optional(types.number, 0),
  timestamps: types.maybe(types.frozen<Timestamps>()),
  currentTimestamp: types.optional(types.frozen<Timestamp>(), doNotJumpTimestamp),
  videoDuration: types.optional(types.number, 0),
  videoUrl: types.optional(types.string, ""),
  isVideoPlaying: types.optional(types.boolean, false),
  isFullscreen: types.optional(types.boolean, false),
  videoLoadedSeconds: types.optional(types.number, 0),
  isPictureInPicture: types.optional(types.boolean, false),
  aVideoIsLoaded: types.optional(types.boolean, false),
  videoCanPlay: types.optional(types.boolean, false),
  isVideoPositionProgress: types.optional(types.boolean, false),
  videoPlayerTimestampsLoaded: types.optional(types.boolean, false),
  VADamageDetections: types.optional(types.array(types.frozen<VADetection>()), []),
  selectedVADetection: types.optional(types.frozen<VADetection>(), emptyVADetection),
  lastScreenshotUri: types.optional(types.string, ""),
  lastScreenshotTimestamp: types.optional(types.number, 0),
  showArea: true,
  showContour: true,
  showBox: true,
  groupCodeFilterOptions: types.optional(types.array(types.frozen<CodeDetection>()), []),
  playerWidth: types.optional(types.number, 0),
  playerHeight: types.optional(types.number, 0)
});

interface VideoProgress {
  playedSeconds: number;
  loadedSeconds: number;
}

export const VideoPlayerServiceModel = VideoPlayerModel.views((self) => ({
  get videoWidth() {
    if (!self.aVideoIsLoaded) return 0;
    if (self.isFullscreen) return "100%";

    return undefined;
  },
  get videoHeight() {
    if (!self.aVideoIsLoaded) return 0;
    if (self.isFullscreen) return "90%";

    return undefined;
  },
  get video() {
    return getEnv(self).video;
  },
  get pictureInPictureVideo() {
    return getEnv(self).pictureInPictureVideo;
  },
  get player() {
    return getEnv(self).player;
  },
  get videoFinishedPlaying() {
    return self.videoProgress === self.videoDuration;
  },
  get videoInView() {
    return self.isPictureInPicture ? getEnv(self).pictureInPictureVideo : getEnv(self).video;
  },
  get videoNotInView() {
    return self.isPictureInPicture ? getEnv(self).video : getEnv(self).pictureInPictureVideo;
  },
  get vaDetectionsWithoutEmptyDetection() {
    return self.VADamageDetections.filter((d) => d.mainCode !== emptyVADetection.mainCode);
  },
}))
  .actions((self) => {
    return {
      enterFullscreen: flow(function* enterFullscreen() {
        if (screenfull.isEnabled && getEnv(self).player && getEnv(self).player.current) {
          const videoFileDOMNode = findDOMNode(getEnv(self).player.current) as Element;
          return yield screenfull.request(videoFileDOMNode);
        }
      }),
      exitFullscreen: flow(function* exitFullscreen() {
        if (screenfull.isEnabled) {
          return yield screenfull.exit();
        }
      }),
      syncVideoAnalysisDiagramPosition(timestamp: number) {
        const videoAnalysisDiagram: any = window.document.getElementById(
          "damages-video-analysis-iframe"
        );
        if (
          videoAnalysisDiagram &&
          videoAnalysisDiagram.contentWindow.update_current_time_indicator
        ) {
          videoAnalysisDiagram.contentWindow.update_current_time_indicator(timestamp);
        }
      },
      resetVideoPlayer() {
        self.isVideoPlaying = false;
        self.isFullscreen = false;
        if (!self.isVideoPositionProgress) {
          self.videoProgress = 0;
          self.videoLoadedSeconds = 0;
        }
        self.isPictureInPicture = false;
        self.currentTimestamp = new Timestamp(emptyTimestamp);
        self.selectedVADetection = emptyVADetection;
      },
      setWidth(width: number) {
        self.playerWidth = width;
      },
      setHeight(height: number) {
        self.playerHeight = height;
      },
      detachVideo() {
        self.isPictureInPicture = true;
        const videoElement = getVideoElementFromReactPlayer(self.video);
        if (videoElement && document.pictureInPictureEnabled) {
          videoElement.requestPictureInPicture().catch(() => {});
        }
      },
      cancelDetachVideo() {
        self.isPictureInPicture = false;
      },
      excludeTimestampsNotInVideoRange() {
        if (self.videoDuration !== 0) {
          self.timestamps = self.timestamps?.filterOutOnesOutOfVideoRange(self.videoDuration);
        }
      },
      setVideoProgress(videoProgress: number) {
        self.videoProgress = Math.round(videoProgress);
      },
      stopVideo() {
        self.isVideoPlaying = false;
      },
      startVideo() {
        self.isVideoPlaying = true;
      },
      setIsFullScreen(isFullscreen: boolean) {
        self.isFullscreen = isFullscreen;
      },
      setVideoCanPlay(videoCanPlay: boolean) {
        self.videoCanPlay = videoCanPlay;
      },
      clearVideoPositionProgress() {
        self.isVideoPositionProgress = false;
      },
      computeVADamageDetections(detectionsAtTime: DetectionsAtTime) {
        let detections: any = {};
        const videoAnalysisDiagram: any = window.document.getElementById(
          "damages-video-analysis-iframe"
        );
        if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.get_current_detections) {
          const vaDetections = videoAnalysisDiagram.contentWindow.get_current_detections(
            self.videoProgress
          );
          detections = vaDetections["Detections"];
        } else {
          detections = detectionsAtTime;
        }
        const computedDetections: VADetection[] = [];

        for (let key in detections) {
          const computedDetection: VADetection = { mainCode: "", c1Code: "", c2Code: "" };
          computedDetection.mainCode = key;

          for (let keyCode in detections[key]) {
            if (keyCode === SecondaryCodeEnum[SecondaryCodeEnum.C1]) {
              computedDetection.c1Code = detections[key][keyCode];
            }
            if (keyCode === SecondaryCodeEnum[SecondaryCodeEnum.C2]) {
              computedDetection.c2Code = detections[key][keyCode];
            }
          }

          computedDetections.push(computedDetection);
        }

        computedDetections.push(emptyVADetection);

        self.VADamageDetections = cast(computedDetections);
      },
      setSelectedVADetection(vaDetection: VADetection) {
        self.selectedVADetection = vaDetection;
      },
      changeShowArea() {
        self.showArea = !self.showArea;
      },
      changeShowContour() {
        self.showContour = !self.showContour;
      },
      changeShowBox() {
        self.showBox = !self.showBox;
      },
      setGroupCodesFilterOptions(codes: any) {
        self.groupCodeFilterOptions = cast([]);
        codes.forEach((code: CodeDetection) =>
          self.groupCodeFilterOptions.push(Object.assign({}, code))
        );
      },
    };
  })
  .actions((self) => {
    return {
      jumpTo(timestamp: number) {
        const roundedTimestamp = Math.round(timestamp);
        if (self.videoInView && self.videoInView.current) {
          self.videoInView.current.seekTo(roundedTimestamp);

          if (self.videoNotInView && self.videoNotInView.current) {
            self.videoNotInView.current.seekTo(roundedTimestamp);
          }

          self.syncVideoAnalysisDiagramPosition(roundedTimestamp);
        }
      },
      loadVideo(url: string) {
        self.videoUrl = url;
        self.aVideoIsLoaded = true;
      },
      handleDuration(duration: number) {
        self.videoDuration = Math.round(duration);
        self.excludeTimestampsNotInVideoRange();
      },
      loadVideoTimestamps(timestamps: Timestamps | undefined) {
        self.resetVideoPlayer();
        self.timestamps = timestamps;
        self.videoPlayerTimestampsLoaded = true;
      },
      initializeVideoWithVideoPositionProgress(videoProgress: number) {
        self.setVideoProgress(videoProgress);
        self.isVideoPositionProgress = true;
      },
    };
  })
  .actions((self) => {
    return {
      loadVideoWithoutReset(url: string) {
        self.videoUrl = url;
        self.isFullscreen = false;
        self.isPictureInPicture = false;

        self.jumpTo(self.videoProgress);
      },
      setCurrentTimestamp(timestamp: number) {
        const newTimestamp =
          self.timestamps?.getTimestampAt(timestamp) ??
          self.timestamps?.getLastTimestampCloseTo(timestamp) ??
          self.timestamps?.getFirstTimestampCloseTo(timestamp);
        if (!newTimestamp) return;

        const currentTimestampOutOfRange = self.timestamps?.outOfRange(self.currentTimestamp);
        const newTimestampInRange = !self.timestamps?.outOfRange(newTimestamp);

        if (
          !newTimestamp.isSameTimeWith(self.currentTimestamp) ||
          (currentTimestampOutOfRange && newTimestampInRange)
        ) {
          self.currentTimestamp = newTimestamp;
          self.jumpTo(timestamp);
        }
      },
      setCurrentTimestampById(id: number) {
        const newTimestamp = self.timestamps?.getById(id);
        if (!newTimestamp) return;

        self.currentTimestamp = newTimestamp;
        self.jumpTo(newTimestamp.timestamp);
      },
      syncVideosOnDetachmentAndAttachment() {
        if (self.isPictureInPicture) {
          self.jumpTo(self.videoProgress || 0);
        }

        if (!self.isPictureInPicture) {
          self.jumpTo(self.videoProgress || 0);
        }
      },
      initializeFullScreen() {
        if (screenfull.isEnabled)
          screenfull.onchange(() =>
            self.setIsFullScreen(screenfull.isEnabled && screenfull.isFullscreen)
          );
      },
      jumpPrevious() {
        self.currentTimestamp =
          self.timestamps?.getPreviousTimestamp(self.currentTimestamp) || doNotJumpTimestamp;

        if (self.currentTimestamp.id !== doNotJumpTimestamp.id)
          self.jumpTo(self.currentTimestamp.timestamp);
      },
      jumpNext() {
        self.currentTimestamp =
          self.timestamps?.getNextTimestamp(self.currentTimestamp) || doNotJumpTimestamp;

        if (self.currentTimestamp.id !== doNotJumpTimestamp.id)
          self.jumpTo(self.currentTimestamp.timestamp);
      },
      resetLastScreenshot() {
        self.lastScreenshotUri = "";
        self.lastScreenshotTimestamp = 0;
      },
    };
  })
  .actions((self) => {
    return {
      afterCreate() {
        self.initializeFullScreen();
        self.syncVideosOnDetachmentAndAttachment();
      },
      handleProgress(videoProgress: VideoProgress) {
        const roundedVideoProgress = Math.round(videoProgress.playedSeconds);
        self.videoProgress = roundedVideoProgress;
        self.videoLoadedSeconds = videoProgress.loadedSeconds;

        self.setCurrentTimestamp(roundedVideoProgress);

        self.syncVideoAnalysisDiagramPosition(roundedVideoProgress);
      },
      takeScreenshotAtCurrentVideoPosition: flow(function* (videoId: number, onTimeCaptured?: (time: number) => void) {
        self.stopVideo();
        try {
          const endpoint = endpoints.get("media_file_screenshot");
          endpoint.routeParams = [videoId, self.videoProgress];
          const result = yield getEnv(self).api.get(endpoint);

          self.lastScreenshotUri = result;
          self.lastScreenshotTimestamp = self.videoProgress;
          if (onTimeCaptured){
            onTimeCaptured(self.videoProgress);
          }
        } catch (e) {
          self.resetLastScreenshot();
        }
      }),
    };
  });

export type VideoPlayerService = Instance<typeof VideoPlayerServiceModel>;
export const videoPlayerService = VideoPlayerServiceModel.create(
  {},
  {
    api: api,
    video: React.createRef<ReactPlayer>(),
    pictureInPictureVideo: React.createRef<ReactPlayer>(),
    player: React.createRef<HTMLDivElement>(),
  }
);

reaction(
  () => videoPlayerService.videoFinishedPlaying,
  () => videoPlayerService.videoFinishedPlaying && videoPlayerService.stopVideo()
);
